import React, { useCallback, useEffect, useMemo, useState } from "react";

import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import ClienteForm from "../components/ClienteForm";
import Toast, { useToast } from "../components/Toast";

import {
  obtenerClientes,
  crearCliente,
  actualizarCliente,
  obtenerClienteDetalle,
} from "../services/clienteService";

const parseEmpresa = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const getEmpresaId = () => {
  const a = parseEmpresa(localStorage.getItem("empresaId"));
  if (a) return a;
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const b = parseEmpresa(user?.empresaId ?? user?.EmpresaId);
    if (b) return b;
  } catch {}
  return null;
};

export default function ClientesPage() {
  const empresaId = getEmpresaId();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [estatus, setEstatus] = useState("A");
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [clienteInicial, setClienteInicial] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const { toasts, closeToast, error: toastError, warning: toastWarning } = useToast();

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  );

  // useCallback evita que se recree en cada render y cause loop
  const cargarLista = useCallback(() => {
    if (!empresaId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    obtenerClientes(empresaId, page, pageSize, search, estatus)
      .then(({ data }) => {
        const payload = data ?? {};
        const lista =
          (Array.isArray(payload.Data) && payload.Data) ||
          (Array.isArray(payload.data) && payload.data) ||
          [];
        const totItems =
          payload.TotalItems ??
          payload.totalItems ??
          payload.Total ??
          lista.length;
        const mapped = lista.map((c) => ({
          id: c.id ?? c.ID ?? c.Id ?? null,
          clave: c.clave ?? c.Clave,
          nombre: c.nombre ?? c.Nombre ?? c.razonSocial ?? c.RazonSocial,
          estatus: c.estatus ?? c.Estatus ?? "A",
        }));
        const newTotalPages = Math.max(1, Math.ceil(totItems / pageSize));
        if (page > newTotalPages) {
          setTotal(totItems);
          setPage(newTotalPages);
          return;
        }
        setRows(mapped);
        setTotal(totItems);
      })
      .catch((e) => {
        toastError(e?.response?.data?.title || e?.message || "No se pudieron cargar los clientes");
      })
      .finally(() => setLoading(false));
  }, [empresaId, page, pageSize, search, estatus]);

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  const columns = [
    { key: "clave", title: "Clave", style: { width: 160 } },
    { key: "nombre", title: "Nombre" },
    {
      key: "estatus",
      title: "Estatus",
      style: { width: 110 },
      render: (r) => (
        <span
          className={`lmd-badge ${r.estatus === "A" ? "lmd-badge-success" : "lmd-badge-neutral"}`}
        >
          {r.estatus === "A" ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      key: "acciones",
      title: "",
      style: { width: 100 },
      render: (r) => (
        <button
          className="lmd-btn lmd-btn-warning lmd-btn-sm"
          onClick={async () => {
            try {
              setLoadingCliente(true);
              const detalle = await obtenerClienteDetalle({
                id: r.id,
                empresaId,
                clave: r.clave,
              });
              setClienteInicial(
                detalle || { clave: r.clave, nombre: r.nombre },
              );
              setShowModal(true);
            } catch {
              toastError("No fue posible cargar el cliente.");
            } finally {
              setLoadingCliente(false);
            }
          }}
        >
          Editar
        </button>
      ),
    },
  ];

  const handleSubmitForm = async (cliente) => {
    try {
      if (!empresaId) {
        toastWarning("No hay empresa seleccionada.");
        return;
      }
      if (cliente?.ID) {
        await actualizarCliente(cliente.ID, cliente, empresaId);
      } else {
        await crearCliente(cliente, empresaId);
      }
      setShowModal(false);
      setClienteInicial(null);
      cargarLista();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.errors && typeof d.errors === "object") {
        toastError(Object.entries(d.errors).map(([k, arr]) => `${k}: ${(arr ?? []).join(" ")}`).join(" | "));
      } else {
        toastError(d?.title || d?.message || err?.message || "No se pudo guardar.");
      }
    }
  };

  const extraRight = (
    <select
      className="lmd-select"
      value={estatus}
      onChange={(e) => {
        setEstatus(e.target.value);
        setPage(1);
      }}
    >
      <option value="">Todos los estatus</option>
      <option value="A">Activo</option>
      <option value="I">Inactivo</option>
    </select>
  );

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader
        title="Clientes"
        subtitle="Gestion del catalogo de clientes"
      />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por nombre o clave..."
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => setPage(1)}
          sizeValue={pageSize}
          onSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
          onAdd={() => {
            setClienteInicial({});
            setShowModal(true);
          }}
          addText="Nuevo Cliente"
          extraRight={extraRight}
        />

        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" />
            Cargando registros...
          </div>
        )}

        <DataTable columns={columns} rows={rows} keyField="clave" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{total}</strong> registros
          </span>
          <div className="lmd-pagination-spacer" />
          <button
            className="lmd-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage(1)}
          >
            «
          </button>
          <button
            className="lmd-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </button>
          <span
            style={{
              padding: "0 10px",
              fontSize: "var(--fs-sm)",
              color: "var(--clr-text-secondary)",
            }}
          >
            Pag. <strong>{page}</strong> / <strong>{totalPages}</strong>
          </span>
          <button
            className="lmd-page-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            ›
          </button>
          <button
            className="lmd-page-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage(totalPages)}
          >
            »
          </button>
        </div>
      </div>

      {showModal && (
        <div className="lmd-modal-overlay" onClick={() => setShowModal(false)}>
          <div
            className="lmd-modal lmd-modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">
                {clienteInicial?.ID ? "Editar Cliente" : "Nuevo Cliente"}
              </h3>
              <button
                className="lmd-modal-close"
                onClick={() => setShowModal(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="lmd-modal-body">
              {loadingCliente ? (
                <div className="lmd-loading-bar">
                  <div className="lmd-spinner" /> Cargando cliente...
                </div>
              ) : (
                <ClienteForm
                  clienteInicial={clienteInicial}
                  onSubmit={handleSubmitForm}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
